import axios from "axios";
import { getApiUrl } from "./environmentService";
import { getAuthUser } from "./authService";

export interface RequestFile {
  name: string;
  size: number;
  type: string;
  url?: string;
}

export interface Request {
  id: string;
  request_type: 'voice_note' | 'link' | 'document' | 'self_interview' | 'professional_interview';
  links: string[];
  files: RequestFile[] | any[];
  context?: string;
  status: 'processing' | 'done' | 'not_used';
  user: {
    id: string;
    name: string;
    email: string;
  };
  created_at: string;
  updated_at: string;
}

const getAuthHeaders = () => ({ Authorization: getAuthUser()?.accessToken });

export const requestsService = {
  async getRequests(): Promise<Request[]> {
    const response = await axios.get(
      `${getApiUrl()}/api/requests`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  async getRequestById(requestId: string): Promise<Request> {
    const response = await axios.get(
      `${getApiUrl()}/api/requests/${requestId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  async createRequest(request: Partial<Request>): Promise<Request> {
    const response = await axios.post(
      `${getApiUrl()}/api/requests`,
      { request },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  async updateRequest(requestId: string, updates: Partial<Request>): Promise<Request> {
    const response = await axios.put(
      `${getApiUrl()}/api/requests/${requestId}`,
      { request: updates },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  async deleteRequest(requestId: string): Promise<void> {
    await axios.delete(
      `${getApiUrl()}/api/requests/${requestId}`,
      { headers: getAuthHeaders() }
    );
  },

  async createVoiceNote(data: { transcription: string; duration: number; files?: File[] }): Promise<{ success: boolean; request_id: string; status: string; created_at: string }> {
    const formData = new FormData();
    
    formData.append('transcription', data.transcription);
    formData.append('duration', data.duration.toString());
    
    if (data.files) {
      data.files.forEach(file => {
        formData.append('files[]', file);
      });
    }

    const response = await axios.post(
      `${getApiUrl()}/api/requests/voice_note`,
      formData,
      { 
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  },

  async createDocument(data: { context?: string; files?: File[] }): Promise<{ success: boolean; request_id: string; status: string; created_at: string }> {
    const formData = new FormData();
    
    if (data.context) {
      formData.append('context', data.context);
    }
    
    if (data.files) {
      data.files.forEach(file => {
        formData.append('files[]', file);
      });
    }

    const response = await axios.post(
      `${getApiUrl()}/api/requests/document`,
      formData,
      { 
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  }
};